import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  createRoom,
  deleteRoomImage,
  getRoomByID,
  RoomImageUpload,
  updateRoom,
} from "./services/room.services";
import {
  clearRoomDetails,
  clearRoomImageID,
  setRoomImageID,
} from "./roomSlice";
import { getFile } from "../../_helper/helper";
import { IProperty } from "../property/propertyModel";
import { getAllActiveProperties } from "../property/services/property.services";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import IconComponent from "../../components/common/IconComponent";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";

const CreateRoom: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const RoomID = location.state?.RoomID;
  const RoomDetail = useAppSelector((state) => state.room.RoomDetail);
  const formLoading = useAppSelector((state) => state.room.formLoading);
  const PropertyList = useAppSelector((state) => state.property.PropertyList);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [RoomDetail]);

  useEffect(() => {
    dispatch(getAllActiveProperties());

    if (RoomID) {
      dispatch(getRoomByID(RoomID));
    }

    return () => {
      dispatch(clearRoomDetails());
    };
  }, [RoomID, dispatch]);

  const handleSubmit = async (values: any) => {
    const formData = {
      RoomTitle: values.RoomTitle ?? "",
      RoomDescription: values.RoomDescription ?? "",
      Rates: values.Rates ? parseFloat(values.Rates) : 0,
      PropertyID: values.PropertyID ? parseInt(values.PropertyID) : 0,
      SequenceNo: values?.SequenceNo ? +values?.SequenceNo : null,
      IsActive: values.IsActive === 1,
      IsFeatured: values.IsFeatured,
    };

    const ImageData = new FormData();
    const Images =
      values?.Images && typeof values?.Images !== "string"
        ? getFile(values?.Images)
        : "";

    try {
      if (RoomID) {
        const editPayload = {
          ...formData,
          ID: RoomID,
        };

        const response = await dispatch(updateRoom(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.Images && typeof values?.Images !== "string") ||
            values.attachments?.length > 0
          ) {
            ImageData.append("Images", Images);
            ImageData.append("ID", RoomID);
            if (values?.attachments && Array.isArray(values.attachments)) {
              values.attachments.forEach((image: any) => {
                if (image.attachment && typeof image.attachment !== "string")
                  return;
                const attachment = getFile(image);
                if (attachment) {
                  ImageData.append("ImageGallery", attachment);
                }
              });
            }

            await dispatch(RoomImageUpload(ImageData));
          }
          navigate("/room");
        }
      } else {
        const response = await dispatch(createRoom(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.Images && typeof values?.Images !== "string") ||
              values.attachments?.length > 0) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Images", Images);
            ImageData.append("ID", response?.payload?.Data?.ID);
            if (values?.attachments && Array.isArray(values.attachments)) {
              values.attachments.forEach((image: any) => {
                if (image.attachment && typeof image.attachment !== "string")
                  return;
                const attachment = getFile(image);
                if (attachment) {
                  ImageData.append("ImageGallery", attachment);
                }
              });
            }

            await dispatch(RoomImageUpload(ImageData));
          }
          navigate("/room");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleDeleteImage = (ID: number) => {
    dispatch(openDialog("deleteRoomImage"));
    dispatch(setRoomImageID(ID));
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <DeleteRoomImageDialog />
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={RoomDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {RoomID ? "Update Room" : "Add Room"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      id={"PropertyID"}
                      name={"PropertyID"}
                      label={"Property Name"}
                      placeholder="Property Name"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={PropertyList?.map((property: IProperty) => {
                        return {
                          value: property?.ID,
                          label: property?.PropertyName,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="RoomTitle"
                      label="Room Title"
                      placeholder="Room Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="right-aligned-field"
                      name="Rates"
                      label="Rate"
                      placeholder="0"
                      min={0}
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="RoomDescription"
                      label="Room Description"
                      placeholder="Room Description"
                      component={FormRichTextField}
                      rows={2}
                      // style={{ height: 200 }}
                    />
                  </GridLayoutItem>
                  {/* <GridLayoutItem>
                    <Field
                      justifyContent="start"
                      name={`Images`}
                      label="Room Image"
                      type="file"
                      accept="image/*"
                      alternateName="Room Image"
                      component={FormImageUpload}
                      astrike={true}
                      validator={requiredArrayValidator}
                    />
                  </GridLayoutItem> */}
                  <GridLayoutItem>
                    <Field
                      justifyContent="start"
                      name={`attachments`}
                      label="Inner Images"
                      type="file"
                      multiple
                      accept="image/*"
                      alternateName="Inner Images"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  {RoomDetail?.RoomInnerimage &&
                    RoomDetail?.RoomInnerimage?.length > 0 && (
                      <GridLayoutItem colSpan={2} className="mt-3">
                        <div className="card shadow-sm border-0">
                          {RoomDetail?.RoomInnerimage?.length > 0 && (
                            <div
                              className="card-body p-2 pt-3 pe-3"
                              style={{
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5
                                  className="mb-0"
                                  style={{
                                    color: "#2c3e50",
                                    fontWeight: "600",
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  Uploaded Images
                                </h5>
                                <span className="badge bg-kendo-primary rounded-pill">
                                  {RoomDetail?.RoomInnerimage?.length || 0}{" "}
                                  files
                                </span>
                              </div>

                              <div className="d-flex flex-wrap gap-3">
                                {RoomDetail?.RoomInnerimage?.map(
                                  (image: any) => (
                                    <div
                                      key={image.ID}
                                      className="position-relative"
                                      style={{
                                        width: "120px",
                                        transition: "all 0.3s ease",
                                      }}
                                    >
                                      <div className="card h-100 border-0 shadow-sm overflow-hidden">
                                        <div className="ratio ratio-1x1 bg-light overflow-hidden">
                                          <div
                                            style={{
                                              position: "absolute",
                                              width: "100%",
                                              height: "auto",
                                              display: "flex",
                                              justifyContent: "end",
                                            }}
                                          >
                                            <Button
                                              type="button"
                                              themeColor={"error"}
                                              fillMode={"solid"}
                                              style={{
                                                position: "relative",
                                                padding: "5px 10px",
                                                width: 30,
                                                height: 30,

                                                color: "#fff",
                                                zIndex: "1",
                                                borderRadius: "0 0 0 5px",
                                              }}
                                              onClick={() =>
                                                handleDeleteImage(image.ID)
                                              }
                                            >
                                              <IconComponent
                                                icon={MdDelete}
                                                className="absolute-position fs-5"
                                              />
                                            </Button>
                                          </div>
                                          <img
                                            className="img-fluid object-fit-cover"
                                            src={image.ImageGallery}
                                            alt="Uploaded document"
                                            style={{
                                              cursor: "pointer",
                                              transition: "transform 0.3s",
                                            }}
                                            onMouseEnter={(e) =>
                                              (e.currentTarget.style.transform =
                                                "scale(1.05)")
                                            }
                                            onMouseLeave={(e) =>
                                              (e.currentTarget.style.transform =
                                                "scale(1)")
                                            }
                                            onClick={() =>
                                              window.open(
                                                `${image.ImageGallery}`,
                                                "_blank",
                                              )
                                            }
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </GridLayoutItem>
                    )}
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="right-aligned-field"
                      name="SequenceNo"
                      label="Sequence No."
                      placeholder="0"
                      format="n0"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => ({
                        value: status?.value,
                        label: status?.label,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      textAlign: "right",
                      marginTop: 20,
                    }}
                  >
                    <ButtonWithLoading
                      label={RoomID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || formLoading}
                      loading={formLoading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => navigate("/room")}
                    >
                      Cancel
                    </Button>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

const DeleteRoomImageDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const location = useLocation();
  const RoomID = location.state?.RoomID;

  const RoomImageID = useAppSelector((state) => state.room.RoomImageID);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteRoomImage(ID));
      if (response.meta.requestStatus === "fulfilled") {
        if (RoomID) {
          dispatch(getRoomByID(RoomID));
        }
        dispatch(closeDialog());
        dispatch(clearRoomImageID());
      } else {
        if (RoomID) {
          dispatch(getRoomByID(RoomID));
        }
        dispatch(closeDialog());
        dispatch(clearRoomImageID());
      }
    }
  };
  return (
    <>
      {dialogName === "deleteRoomImage" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Image?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Image?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(RoomImageID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default CreateRoom;
